/**
 * Composable for managing pan and zoom interactions on images
 */

import { ref, computed, type Ref, type ComputedRef } from 'vue'
import type { PanZoomState } from '../types'
import type { Dimensions } from '../../types/base'
import {
  MIN_ZOOM,
  MAX_ZOOM,
  ZOOM_STEP,
  clampZoom,
  clampPan,
  incrementZoom,
  decrementZoom,
  calculateZoomOffset,
  type ContainerDimensions,
} from '../utils/image-utils'

export interface UsePanZoomOptions {
  initialZoom?: number
  minZoom?: number
  maxZoom?: number
  zoomStep?: number
  onZoomChange?: (state: PanZoomState) => void
  onPanChange?: (panX: number, panY: number) => void
}

export interface UsePanZoomReturn {
  // State
  zoom: Ref<number>
  panX: Ref<number>
  panY: Ref<number>
  isZoomed: ComputedRef<boolean>
  state: ComputedRef<PanZoomState>

  // Zoom methods
  zoomIn: () => void
  zoomOut: () => void
  setZoom: (level: number) => void
  resetZoom: () => void
  zoomToPoint: (level: number, pointX: number, pointY: number) => void

  // Pan methods
  setPan: (x: number, y: number) => void
  resetPan: () => void
  applyPanDelta: (deltaX: number, deltaY: number) => void

  // Dimensions
  setImageDimensions: (dimensions: Dimensions) => void
  setContainerDimensions: (dimensions: ContainerDimensions) => void

  // Combined reset
  reset: () => void
}

/**
 * Composable for managing pan and zoom state
 */
export function usePanZoom(options: UsePanZoomOptions = {}): UsePanZoomReturn {
  const {
    initialZoom = 1,
    minZoom = MIN_ZOOM,
    maxZoom = MAX_ZOOM,
    zoomStep = ZOOM_STEP,
    onZoomChange,
    onPanChange,
  } = options

  // State
  const zoom = ref(initialZoom)
  const panX = ref(0)
  const panY = ref(0)

  // Dimensions (needed for pan clamping)
  const imageDimensions = ref<Dimensions>({ width: 0, height: 0 })
  const containerDimensions = ref<ContainerDimensions>({ width: 0, height: 0 })

  // Computed
  const isZoomed = computed(() => zoom.value > 1)

  const state = computed<PanZoomState>(() => ({
    zoom: zoom.value,
    panX: panX.value,
    panY: panY.value,
    isZoomed: isZoomed.value,
  }))

  /**
   * Notifies zoom change callback
   */
  function notifyZoomChange(): void {
    onZoomChange?.(state.value)
  }

  /**
   * Notifies pan change callback
   */
  function notifyPanChange(): void {
    onPanChange?.(panX.value, panY.value)
  }

  /**
   * Updates pan with clamping
   */
  function updatePan(newPanX: number, newPanY: number): void {
    const clamped = clampPan(
      newPanX,
      newPanY,
      zoom.value,
      imageDimensions.value,
      containerDimensions.value,
    )
    const changed = clamped.panX !== panX.value || clamped.panY !== panY.value
    panX.value = clamped.panX
    panY.value = clamped.panY
    if (changed) {
      notifyPanChange()
    }
  }

  /**
   * Updates zoom with clamping and pan adjustment
   */
  function updateZoom(newZoom: number): void {
    const clampedZoom = clampZoom(newZoom, minZoom, maxZoom)
    if (clampedZoom !== zoom.value) {
      zoom.value = clampedZoom
      // Re-clamp pan after zoom change
      updatePan(panX.value, panY.value)
      notifyZoomChange()
    }
  }

  // Zoom methods
  function zoomIn(): void {
    updateZoom(incrementZoom(zoom.value, zoomStep, maxZoom))
  }

  function zoomOut(): void {
    updateZoom(decrementZoom(zoom.value, zoomStep, minZoom))
  }

  function setZoom(level: number): void {
    updateZoom(level)
  }

  function resetZoom(): void {
    updateZoom(1)
  }

  function zoomToPoint(level: number, pointX: number, pointY: number): void {
    const clampedLevel = clampZoom(level, minZoom, maxZoom)
    if (clampedLevel !== zoom.value) {
      const newPan = calculateZoomOffset(
        zoom.value,
        clampedLevel,
        panX.value,
        panY.value,
        pointX,
        pointY,
      )
      zoom.value = clampedLevel
      updatePan(newPan.panX, newPan.panY)
      notifyZoomChange()
    }
  }

  // Pan methods
  function setPan(x: number, y: number): void {
    updatePan(x, y)
  }

  function resetPan(): void {
    updatePan(0, 0)
  }

  function applyPanDelta(deltaX: number, deltaY: number): void {
    updatePan(panX.value + deltaX, panY.value + deltaY)
  }

  // Dimension setters
  function setImageDimensions(dimensions: Dimensions): void {
    imageDimensions.value = dimensions
    // Re-clamp pan after dimensions change
    updatePan(panX.value, panY.value)
  }

  function setContainerDimensions(dimensions: ContainerDimensions): void {
    containerDimensions.value = dimensions
    // Re-clamp pan after dimensions change
    updatePan(panX.value, panY.value)
  }

  // Combined reset
  function reset(): void {
    zoom.value = 1
    panX.value = 0
    panY.value = 0
    notifyZoomChange()
    notifyPanChange()
  }

  return {
    // State
    zoom,
    panX,
    panY,
    isZoomed,
    state,

    // Zoom methods
    zoomIn,
    zoomOut,
    setZoom,
    resetZoom,
    zoomToPoint,

    // Pan methods
    setPan,
    resetPan,
    applyPanDelta,

    // Dimensions
    setImageDimensions,
    setContainerDimensions,

    // Combined reset
    reset,
  }
}
